// SCR-STAFF-304 (نمای ارائه‌دهنده) — برنامه من (US-SCH-005، 062): فهرست امروز (COMP-PROVIDER-QUEUE)، نوبت‌ها و استثناهای بازه و ثبت مرخصی
// خود با sch.schedule.manage_own (⚙️). مرخصی دارای نوبت تا تأیید مدیر «در انتظار تأیید» می‌ماند و اثری بر دسترس‌پذیری ندارد.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type ScheduleExceptionView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, LoadingState, PageHeader, toJalali } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { formatJalaliInput, parseJalaliInput } from '../patients/common';
import { addDays, formatDay, formatTime, schKeys, useActiveBranch, useBookingOptions } from './common';
import { ProviderQueue } from './ProviderQueue';

export const SCR_STAFF_304_OWN = 'SCR-STAFF-304';

const OWN_TYPES = ['Leave', 'PartialLeave'] as const;

export function MySchedulePage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const { branchId } = useActiveBranch();
  const options = useBookingOptions(branchId);
  const today = options.data?.today ?? null;
  const timeZone = options.data?.timeZone;
  const [requesting, setRequesting] = useState(false);
  const [date, setDate] = useState('');
  const [type, setType] = useState<(typeof OWN_TYPES)[number]>('Leave');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const tenantId = session?.tenantId;
  const schedule = useQuery({
    queryKey: schKeys.mySchedule(tenantId, today),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/my/schedule', {
          params: { query: { from: today ?? undefined, to: today ? addDays(today, 30) : undefined } },
        }),
      ),
    enabled: today !== null,
  });
  const request = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/my/schedule-exceptions', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            providerId: null,
            branchId,
            date: parseJalaliInput(date) ?? null,
            type,
            start: type === 'PartialLeave' ? start || null : null,
            end: type === 'PartialLeave' ? end || null : null,
            roomId: null,
            reason: reason.trim() || null,
            confirmImpact: true,
          },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (exception: ScheduleExceptionView) => {
      setRequesting(false);
      setNotice(exception.status === 'PendingApproval' ? t('sch:my.pending_notice') : t('sch:my.saved_notice'));
      void queryClient.invalidateQueries({ queryKey: schKeys.all(tenantId) });
    },
  });
  const data = schedule.data;
  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_304_OWN}>
      <PageHeader
        titleKey="sch:my.title"
        actions={
          data?.canManage ? (
            <Button
              variant="contained"
              onClick={() => {
                setDate(formatJalaliInput(today));
                setRequesting(true);
              }}
              data-testid="my-request-leave"
            >
              {t('sch:my.request_leave')}
            </Button>
          ) : undefined
        }
      />
      {notice ? (
        <Alert severity="info" onClose={() => setNotice(null)} data-testid="my-notice">
          {notice}
        </Alert>
      ) : null}
      <ProviderQueue timeZone={timeZone} />
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" component="h2" gutterBottom>
            {t('sch:my.exceptions')}
          </Typography>
          {schedule.isPending ? (
            <LoadingState rows={2} />
          ) : schedule.error ? (
            <ErrorState error={schedule.error} compact />
          ) : (data?.exceptions ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('sch:my.no_exceptions')}
            </Typography>
          ) : (
            <List dense data-testid="my-exceptions">
              {(data?.exceptions ?? []).map((exception) => (
                <ListItem key={exception.id} divider>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>
                          {formatDay(exception.date)} · {t(`sch:exception_type.${exception.type}`)}
                        </span>
                        {exception.status === 'PendingApproval' ? (
                          <Chip size="small" color="warning" label={t('sch:my.pending')} data-testid="my-exception-pending" />
                        ) : null}
                      </Stack>
                    }
                    secondary={exception.reason ?? undefined}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" component="h2" gutterBottom>
            {t('sch:my.appointments')}
          </Typography>
          {(data?.appointments ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('sch:my.no_appointments')}
            </Typography>
          ) : (
            <List dense>
              {(data?.appointments ?? []).map((appointment) => (
                <ListItem key={appointment.id} divider>
                  <ListItemText
                    primary={`${toJalali(appointment.start, { timeZone, pattern: 'EEEE d MMMM' })} · ${formatTime(appointment.start, timeZone)}`}
                    secondary={appointment.patientName ?? undefined}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <FormDialog
        open={requesting}
        titleKey="sch:my.request_leave"
        onSubmit={() => request.mutate()}
        onClose={() => setRequesting(false)}
        isDirty={reason !== ''}
        isSubmitting={request.isPending}
        submitError={request.error}
      >
        <TextField
          required
          label={t('sch:schedules.date')}
          value={date}
          onChange={(event) => setDate(event.target.value)}
          placeholder={t('sch:schedules.date_placeholder')}
          inputProps={{ dir: 'ltr', 'data-testid': 'my-leave-date' }}
        />
        <TextField select label={t('sch:my.type')} value={type} onChange={(event) => setType(event.target.value as (typeof OWN_TYPES)[number])}>
          {OWN_TYPES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`sch:exception_type.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        {type === 'PartialLeave' ? (
          <Stack direction="row" spacing={2}>
            <TextField label={t('sch:waitlist.time_from')} value={start} onChange={(event) => setStart(event.target.value)} placeholder="09:00" inputProps={{ dir: 'ltr' }} />
            <TextField label={t('sch:waitlist.time_to')} value={end} onChange={(event) => setEnd(event.target.value)} placeholder="12:00" inputProps={{ dir: 'ltr' }} />
          </Stack>
        ) : null}
        <TextField label={t('sch:my.reason')} value={reason} onChange={(event) => setReason(event.target.value)} inputProps={{ maxLength: 500, 'data-testid': 'my-leave-reason' }} />
        <Alert severity="info">{t('sch:my.approval_hint')}</Alert>
      </FormDialog>
    </Stack>
  );
}
